// The warehouse query card's networking and local edit state (Page → Hook →
// Component: the card itself fetches nothing).
//
// Both routes are the browser's, under the user's session token, and run in
// goapi: no agent turn and no AI credits are involved. Run and Save are open
// to the chat's owner when the owner is a Manager; the server answers 403 {error}
// to anyone else, and the card shows that sentence.
import { useState } from 'react'
import axios from 'axios'
import { API_BASE_URL } from '../../constants/system'
import {
  viewFromEvent,
  viewFromResult,
  type WarehouseQueryEvent,
  type WarehouseQueryResult,
  type WarehouseResultView,
  type WarehouseSavedQuery,
} from './warehouseQueryTypes'

export interface UseWarehouseQueryCardArgs {
  customer: string | undefined
  sessionId: string
  event: WarehouseQueryEvent
  /** The SQL to start from instead of the event's (the Notebook's saved edit). */
  initialSql?: string
}

export interface WarehouseQueryCardState {
  sql: string
  setSql: (sql: string) => void
  /** The SQL differs from what the event ran. */
  edited: boolean
  view: WarehouseResultView
  running: boolean
  runError: string
  handleRun: () => Promise<void>
  saving: boolean
  saveError: string
  /** The name of the query just saved, until the SQL changes again. */
  savedName: string
  handleSave: (name: string, description: string) => Promise<boolean>
}

const base = (customer: string | undefined, sessionId: string) =>
  `${API_BASE_URL}/agent/${encodeURIComponent(customer ?? '')}/session/${encodeURIComponent(sessionId)}/warehouse`

function sentence(err: unknown, fallback: string): string {
  const data = (err as { response?: { data?: { error?: unknown } } })?.response?.data
  return typeof data?.error === 'string' && data.error ? data.error : fallback
}

export function useWarehouseQueryCard({ customer, sessionId, event, initialSql }: UseWarehouseQueryCardArgs): WarehouseQueryCardState {
  const [sql, setSqlState] = useState(initialSql ?? event.sql)
  const [view, setView] = useState<WarehouseResultView>(() => viewFromEvent(event))
  const [running, setRunning] = useState(false)
  const [runError, setRunError] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [savedName, setSavedName] = useState('')

  const setSql = (next: string) => {
    setSqlState(next)
    setSavedName('')
  }

  const handleRun = async () => {
    if (running || !sql.trim()) return
    setRunning(true)
    setRunError('')
    try {
      const res = await axios.post<WarehouseQueryResult>(`${base(customer, sessionId)}/query`, {
        connection_id: event.connection_id,
        sql,
      })
      setView(viewFromResult(res.data))
    } catch (err) {
      setRunError(sentence(err, 'The query could not be run.'))
    } finally {
      setRunning(false)
    }
  }

  const handleSave = async (name: string, description: string): Promise<boolean> => {
    if (saving) return false
    setSaving(true)
    setSaveError('')
    try {
      const res = await axios.post<{ saved_query: WarehouseSavedQuery }>(`${base(customer, sessionId)}/saved-queries`, {
        connection_id: event.connection_id,
        name,
        description,
        sql,
      })
      setSavedName(res.data.saved_query?.name || name)
      return true
    } catch (err) {
      setSaveError(sentence(err, 'The query could not be saved.'))
      return false
    } finally {
      setSaving(false)
    }
  }

  return { sql, setSql, edited: sql !== event.sql, view, running, runError, handleRun, saving, saveError, savedName, handleSave }
}

export default useWarehouseQueryCard
